import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Skeleton } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/Text";
import { ReadStrengthTag } from "./ReadStrengthTag";
import { SafeAnalystMarkdown } from "./SafeAnalystMarkdown";
import { StorylineContextLine } from "./StorylineContextLine";
import { StorylineCutRows } from "./StorylineCutRows";
import { StorylineFinding } from "./StorylineFinding";
import { StorylineTitle } from "./StorylineTitle";
import { buildFactNumbering } from "./analystFactNumbering";
import { assertNeverStorylineState } from "@/features/studies/report/reportViewState";
import {
  exhibitLinkState,
  findingSlideId,
  readableCuts,
} from "@/features/studies/report/storyline";
import { selectPlottedFindings } from "@/features/studies/report/storylinePlots";

import type { ReactNode } from "react";
import type {
  MetricResult,
  QualitativeThemePackage,
} from "@digital-twins/shared-types/reporting";
import type {
  StorylineReadyViewState,
  StorylineViewState,
} from "@/features/studies/report/reportViewState";

interface StorylineHeaderProps {
  state: StorylineViewState;
  metricResults?: Readonly<Record<string, MetricResult>> | null;
  /** Theme packages, for the findings that plot coded themes. */
  qualitativeResults?: Readonly<Record<string, QualitativeThemePackage>> | null;
  /** The study name: the title shown when the storyline has none. */
  studyName?: string | null;
  /** Pager numbers (visible index + 1) by sequence item id, for slide links. */
  slideNumbers: ReadonlyMap<string, number>;
  onOpenSlide?: (slideId: string) => void;
}

const SECTION_TITLE_CLASS =
  "font-sans text-lg font-semibold text-foreground";

function noFactToOpen() {}

/**
 * The storyline as the analyst tab's first read: a thread header, not a
 * message. Loading shows a quiet skeleton; a failed or malformed storyline
 * renders nothing, so the tab keeps today's empty state.
 */
export function StorylineHeader(props: StorylineHeaderProps) {
  const { state } = props;

  switch (state.kind) {
    case "storyline_loading":
      return <StorylineLoading />;
    case "storyline_ready":
      return <StorylineReady {...props} state={state} />;
    case "storyline_failed":
    case "storyline_malformed":
      return null;
    default:
      return assertNeverStorylineState(state);
  }
}

function StorylineLoading() {
  const { t } = useTranslation("app");

  return (
    <div
      className="flex flex-col gap-3"
      data-testid="storyline-loading"
      aria-busy="true"
    >
      <Text weight="medium">{t("studyReport.storyline.loadingTitle")}</Text>
      <Text size="sm" color="muted" className="-mt-2">
        {t("studyReport.storyline.loadingBody")}
      </Text>
      <Skeleton className="h-6 w-4/5" />
      <Skeleton className="h-16 w-full" />
      <Skeleton className="h-16 w-11/12" />
    </div>
  );
}

interface StorylineReadyProps extends StorylineHeaderProps {
  state: StorylineReadyViewState;
}

function StorylineSection(props: {
  title: string;
  dataTestId: string;
  children: ReactNode;
}) {
  const { title, dataTestId, children } = props;

  return (
    <section className="flex flex-col gap-3" data-testid={dataTestId}>
      <h3 className={SECTION_TITLE_CLASS}>{title}</h3>
      {children}
    </section>
  );
}

function StorylineReady(props: StorylineReadyProps) {
  const {
    state,
    metricResults,
    qualitativeResults,
    studyName,
    slideNumbers,
    onOpenSlide,
  } = props;

  const { t } = useTranslation("app");

  const { storyline, deckLinks } = state;
  // Which findings plot, chosen once for the whole storyline (cap 3).
  const plots = useMemo(
    () =>
      new Map(
        selectPlottedFindings(storyline, { metricResults, qualitativeResults }).map(
          (entry) => [entry.findingId, entry.plot],
        ),
      ),
    [storyline, metricResults, qualitativeResults],
  );
  const findings = storyline.findings;
  const contrary = storyline.contraryEvidence ?? [];
  const openItems = [
    ...(storyline.openQuestions ?? []).map((question) => question.text),
    ...(storyline.movedToOpenQuestions ?? []).map((finding) => finding.headline),
  ];

  return (
    <div className="flex flex-col gap-6" data-testid="storyline-header">
      <div className="flex flex-col gap-1">
        <StorylineContextLine brandContext={storyline.brandContext} />
        <StorylineTitle title={storyline.title} fallback={studyName} />
        <div
          className="text-lg leading-relaxed [&_p]:text-lg"
          data-testid="storyline-thesis"
        >
          <SafeAnalystMarkdown
            content={storyline.thesis}
            numbering={buildFactNumbering(storyline.thesis, [])}
            onOpenFact={noFactToOpen}
          />
        </div>
      </div>

      {findings.length > 0 ? (
        <StorylineSection
          title={t("studyReport.storyline.findingsTitle")}
          dataTestId="storyline-findings"
        >
          <ol className="flex flex-col gap-5">
            {findings.map((finding, index) => (
              <StorylineFinding
                key={finding.findingId}
                storyline={storyline}
                finding={finding}
                index={index}
                linkState={exhibitLinkState(
                  findingSlideId(storyline, finding.findingId),
                  deckLinks,
                  slideNumbers,
                )}
                metricResults={metricResults}
                plot={plots.get(finding.findingId) ?? null}
                onOpenSlide={onOpenSlide}
              />
            ))}
          </ol>
        </StorylineSection>
      ) : null}

      {contrary.length > 0 ? (
        <StorylineSection
          title={t("studyReport.storyline.contraryTitle")}
          dataTestId="storyline-contrary"
        >
          <ul className="flex flex-col gap-4">
            {contrary.map((item, index) => (
              <li
                key={index}
                className="flex flex-col gap-2"
                data-testid={`storyline-contrary-${index}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
                  <Text className="min-w-0 flex-1 leading-relaxed">{item.text}</Text>
                  {item.readStrength ? (
                    <ReadStrengthTag
                      readStrength={item.readStrength}
                      dataTestId={`storyline-contrary-read-${index}`}
                    />
                  ) : null}
                </div>
                {readableCuts(storyline, item.cutRequestIds ?? []).map((cut) => (
                  <StorylineCutRows key={cut.requestId} cut={cut} />
                ))}
              </li>
            ))}
          </ul>
        </StorylineSection>
      ) : null}

      {openItems.length > 0 ? (
        <StorylineSection
          title={t("studyReport.storyline.openTitle")}
          dataTestId="storyline-open-questions"
        >
          <ul className="flex list-disc flex-col gap-2 pl-5">
            {openItems.map((text, index) => (
              <li
                key={index}
                className="leading-relaxed text-foreground"
                data-testid={`storyline-open-question-${index}`}
              >
                {text}
              </li>
            ))}
          </ul>
        </StorylineSection>
      ) : null}
    </div>
  );
}
